import React, { useState } from 'react';
import { Linkedin, Facebook, Twitter, Link as LinkIcon, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const WhatsAppIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export default function SocialShareButtons({ 
  url, 
  title, 
  description, 
  variant = 'default',
  className = '' 
}) {
  const [copied, setCopied] = useState(false);

  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const shareText = title || 'Check out Clinivo - Healthcare Jobs & Nursing Careers';
  const shareDesc = description || '';

  const handleShare = (platform) => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedText = encodeURIComponent(shareText);
    const encodedDesc = encodeURIComponent(shareDesc);

    let shareLink = '';
    switch (platform) {
      case 'linkedin':
        shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
      case 'facebook':
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'twitter':
        shareLink = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
        break;
      case 'whatsapp':
        shareLink = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
        return;
      case 'native':
        if (navigator.share) {
          navigator.share({ title: shareText, text: shareDesc, url: shareUrl }).catch(() => {});
        }
        return;
    }
    if (shareLink) window.open(shareLink, '_blank', 'width=600,height=500,noopener,noreferrer');
  };

  const buttons = [
    { key: 'native', label: 'Share', icon: <Share2 className="h-4 w-4" />, color: 'hover:bg-slate-100 hover:border-slate-400 dark:hover:bg-slate-800', mobileOnly: true },
    { key: 'linkedin', label: 'LinkedIn', icon: <Linkedin className="h-4 w-4" />, color: 'hover:bg-blue-50 hover:border-blue-400 hover:text-blue-600 dark:hover:bg-blue-900/20' },
    { key: 'facebook', label: 'Facebook', icon: <Facebook className="h-4 w-4" />, color: 'hover:bg-blue-50 hover:border-blue-400 hover:text-blue-600 dark:hover:bg-blue-900/20' },
    { key: 'twitter', label: 'X/Twitter', icon: <Twitter className="h-4 w-4" />, color: 'hover:bg-slate-100 hover:border-slate-400 dark:hover:bg-slate-800' },
    { key: 'whatsapp', label: 'WhatsApp', icon: <WhatsAppIcon />, color: 'hover:bg-green-50 hover:border-green-400 hover:text-green-600 dark:hover:bg-green-900/20' },
    { key: 'copy', label: copied ? 'Copied!' : 'Copy Link', icon: <LinkIcon className="h-4 w-4" />, color: 'hover:bg-slate-100 hover:border-slate-400 dark:hover:bg-slate-800' },
  ];

  const visibleButtons = buttons.filter(b => !b.mobileOnly || (typeof navigator !== 'undefined' && navigator.share));

  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-1.5 ${className}`}>
        {visibleButtons.map(btn => (
          <button
            key={btn.key}
            onClick={() => handleShare(btn.key)}
            title={btn.label}
            aria-label={`Share on ${btn.label}`}
            className={`flex items-center justify-center h-9 w-9 rounded-lg border border-slate-200 text-slate-500 transition-all ${btn.color} dark:border-slate-700 dark:text-slate-400`}
          >
            {btn.icon}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {visibleButtons.map(btn => (
          <button
            key={btn.key}
            onClick={() => handleShare(btn.key)}
            title={btn.label}
            aria-label={`Share on ${btn.label}`}
            className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border border-slate-200 text-slate-500 transition-all ${btn.color} dark:border-slate-700 dark:text-slate-400`}
          >
            {btn.icon}
            <span className="text-xs font-medium">{btn.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}